if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }

      if (this.dataset.price) {
        StickyAtcPanel.bindPackage();
        StickyAtcPanel.bindAddonTitles();
        // variant-picker.js swaps this panel for a fresh copy: carry the ticked state over.
        this.updatePackage();
      }
    }

    /**
     * Selleasy add-on checkboxes: when any are ticked the panel shows the package total, the
     * add-on thumbnails and "Lägg paket i varukorgen". Selleasy adds the ticked add-ons itself
     * when this button submits the main form. Bound once on document and always applied to the
     * live panel(s), because the panel element is replaced on variant/product updates.
     */
    static bindPackage() {
      if (StickyAtcPanel.packageBound) return;
      StickyAtcPanel.packageBound = true;

      const update = () => document.querySelectorAll('sticky-atc-panel[data-price]')
        .forEach((panel) => panel.updatePackage?.());
      // Selleasy sets .checked from its own click handler and fires no change event, so react
      // to clicks/keys on its cards (and quantity changes) once those handlers have run.
      // Selleasy can take several hundred ms to flip .checked on the first tap, so check every
      // 100ms for 2s.
      let timer;
      const onInput = (evt) => {
        if (!evt.target.closest?.('.lb-product-card, quantity-input, .qty-input')) return;
        clearInterval(timer);
        let runs = 0;
        timer = setInterval(() => {
          update();
          runs += 1;
          if (runs >= 20) clearInterval(timer);
        }, 100);
      };
      // The bar's stepper (mobile) drives the form's quantity input, which is what gets submitted.
      // Registered before onInput so the package price reads the synced value. On `input` too:
      // typed digits only fire `change` on blur, and the poll would otherwise write the form's
      // old value back into the bar while the customer is typing.
      const syncQty = (evt) => {
        if (!evt.target.matches?.('.sticky-atc-panel__qty input')) return;
        const main = document.querySelector('.js-product-form-main [name="quantity"]');
        if (main && evt.target.value !== '') main.value = evt.target.value;
      };
      ['input', 'change'].forEach((type) => document.addEventListener(type, syncQty));
      ['click', 'keyup', 'change', 'input'].forEach((type) => document.addEventListener(type, onInput));
      // Boxes Selleasy ticks by default once it has rendered.
      window.addEventListener('load', () => setTimeout(update, 1500));

      // Add-on stock (owner, 2026-10-08): Shopify adds only what is in stock and answers
      // Selleasy's add-on request with a 422, while the product itself still goes in, so the
      // customer never heard. Watch (never change) that request; on a 422 compare the cart with
      // the quantities from before and say what is missing, one line per add-on:
      // "Bara 3 st av X i lager – resten lades inte till." (Selleasy hides sold-out add-ons.)
      let before = new Map();
      const cartQty = async () => {
        const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
        const qty = new Map();
        cart.items.forEach((item) => qty.set(item.variant_id, (qty.get(item.variant_id) || 0) + item.quantity));
        return qty;
      };
      const remember = () => cartQty().then((qty) => { before = qty; }).catch(() => {});
      remember();
      ['on:cart:add', 'on:line-item:change'].forEach((type) => {
        document.addEventListener(type, () => setTimeout(remember, 3000));
      });
      // A new press clears the note. Only the customer's own press: Selleasy re-fires a
      // (synthetic) click on the button after its add-on request, which would wipe the note.
      document.addEventListener('click', (evt) => {
        if (evt.isTrusted && evt.target.closest?.('.js-product-form-main button[name="add"], sticky-atc-panel button[name="add"]')) {
          StickyAtcPanel.stockNote([]);
        }
      }, true);
      const nativeFetch = window.fetch;
      window.fetch = function watchedFetch(input, init) {
        const result = nativeFetch.apply(this, arguments);
        const url = typeof input === 'string' ? input : input?.url || '';
        if (!url.includes('/cart/add') || !url.includes('src=selleasy') || typeof init?.body !== 'string') return result;
        let asked = [];
        try {
          asked = JSON.parse(init.body).items || [];
        } catch (e) {
          return result;
        }
        StickyAtcPanel.stockNote([]);
        result.then(async (res) => {
          // Refresh the baseline after an add that went through: if the product's own add then
          // fails, no cart event fires, and the next press would count these add-ons again.
          if (res.status !== 422) { setTimeout(remember, 1000); return; }
          const now = await cartQty();
          const panel = document.querySelector('sticky-atc-panel[data-stock-partial]');
          if (!panel) return;
          const lines = asked.flatMap((item) => {
            const added = (now.get(item.id) || 0) - (before.get(item.id) || 0);
            if (added >= item.quantity) return [];
            const row = document.querySelector(`.lb-widget-pal .lb-product-card[lb-variant-id="${item.id}"]`);
            const name = row?.querySelector('.lb-card-title')?.textContent.trim() || '';
            const have = now.get(item.id) || 0;
            if (!have || !name) return [];
            return [panel.dataset.stockPartial.replace('[qty]', have).replace('[name]', name)];
          });
          StickyAtcPanel.stockNote(lines);
          before = now;
        }).catch(() => {});
        return result;
      };
      // Selleasy may untick its boxes after the add without a change event. Wait out the
      // 1.8s green "Tillagd" label (product-form.js) so it restores before we relabel.
      // A repeat add of something already in the cart fires on:line-item:change instead.
      ['on:cart:add', 'on:line-item:change'].forEach((type) => {
        document.addEventListener(type, () => setTimeout(update, 2500));
      });
    }

    /**
     * Selleasy add-on rows show "brand product-model product-type" from the add-on's
     * metafields (templates/product.addon-title.liquid) instead of the full product name.
     * Keeps Selleasy's name when all three are empty. Selleasy renders (and may re-render)
     * its cards late, so an observer re-applies the name.
     */
    static bindAddonTitles() {
      if (StickyAtcPanel.addonTitlesBound) return;
      StickyAtcPanel.addonTitlesBound = true;

      const names = new Map();
      const nameFor = (handle) => {
        if (!names.has(handle)) {
          const url = `${window.Shopify?.routes?.root || '/'}products/${handle}?view=addon-title`;
          names.set(handle, fetch(url)
            .then((res) => (res.ok ? res.json() : { parts: [] }))
            .then((data) => (data.parts || []).filter(Boolean).join(' '))
            .catch(() => ''));
        }
        return names.get(handle);
      };

      const apply = () => {
        // Selleasy formats prices itself ("179,00 kr"); drop ",00" like the rest of the theme,
        // keep real öre ("152,15 kr"). Its package maths reads lb-default-price, not this text.
        document.querySelectorAll('.lb-widget-pal .money, .lb-popup .money').forEach((el) => {
          const text = el.textContent.replace(/[,.]00(?=\D*$)/, '');
          if (text !== el.textContent) el.textContent = text;
        });
        document.querySelectorAll('.lb-product-card[lb-product-handle]').forEach((card) => {
          nameFor(card.getAttribute('lb-product-handle')).then((name) => {
            const title = card.querySelector('.lb-card-title');
            if (name && title && title.textContent !== name) {
              title.title = title.textContent;
              title.textContent = name;
            }
          });
        });
      };

      let queued = false;
      // A tap anywhere on a row ticks it (image, price, empty space); only the name opens
      // Selleasy's product popup. Captured on document so Selleasy's own image/row handlers
      // never see the tap; the custom checkbox gets the click Selleasy toggles on.
      document.addEventListener('click', (evt) => {
        const row = evt.target.closest?.('.lb-widget-pal .lb-flat-card');
        // Leave the name (opens the popup), the checkbox and any real control (a Selleasy
        // variant select, a link) to their own handlers.
        if (!row || evt.target.closest('lb-card-title, card-checkbox, lb-quantity-picker, select, input, button, a, label')) return;
        evt.preventDefault();
        evt.stopPropagation();
        row.querySelector('card-checkbox .lb-custom-checkbox')?.click();
      }, true);

      // Selleasy's product popup "locks" the page with overflow: hidden on <body>
      // (lb-popup-prevent-scroll), which phones - iOS Safari above all - ignore for
      // touch scrolling, so the page scrolled behind the popup. While that class is on,
      // pin the page with position: fixed and put it back where it was on close.
      let lockedAt = null;
      new MutationObserver(() => {
        const locked = document.body.classList.contains('lb-popup-prevent-scroll');
        if (locked && lockedAt === null) {
          lockedAt = window.scrollY;
          Object.assign(document.body.style, { position: 'fixed', top: `-${lockedAt}px`, left: '0', right: '0' });
        } else if (!locked && lockedAt !== null) {
          ['position', 'top', 'left', 'right'].forEach((prop) => document.body.style.removeProperty(prop));
          window.scrollTo(0, lockedAt);
          lockedAt = null;
        }
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

      // Body, not the product section: Selleasy's popup is appended outside it.
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; apply(); });
      }).observe(document.body, { childList: true, subtree: true });
      apply();
    }

    /** Shows (or with [] clears) the add-on stock lines under the buy button and in the bar. */
    static stockNote(lines) {
      document.querySelectorAll('.addon-stock-note').forEach((el) => el.remove());
      if (!lines.length) return;
      const note = (live) => {
        const el = document.createElement('p');
        el.className = 'addon-stock-note';
        if (live) el.setAttribute('role', 'status');
        else el.setAttribute('aria-hidden', 'true');
        el.textContent = lines.join(' ');
        return el;
      };
      document.querySelector('.js-product-form-main .product-info__add-to-cart')?.after(note(true));
      // A role=status inserted with its text is often not read out; the shared region is.
      customElements.get('product-form')?.announce?.(lines.join(' '));
      document.querySelector('sticky-atc-panel:not(.sticky-atc-panel--out) .card--sticky-atc')?.before(note(false));
    }

    updatePackage() {
      const btn = this.querySelector('button[name="add"]');
      const media = this.querySelector('.card__media');
      const pkg = this.querySelector('.sticky-atc-panel__package');
      if (!btn || !pkg || btn.classList.contains('is-added')) return;
      btn.dataset.defaultText = btn.dataset.defaultText || btn.textContent;

      const picked = [...document.querySelectorAll('.lb-widget-pal .lb-product-card')]
        .filter((card) => card.querySelector('.lb-card-checkbox')?.checked);
      // A row without Selleasy's qty picker (setting off, or renamed) goes in once, not × qty.
      const sum = (attr, fallback, qty) => picked.reduce((total, card) => {
        const el = card.querySelector(attr) || card.querySelector(fallback);
        const times = card.querySelector('.lb-qty-count') ? qty : 1;
        return total + times * (Number(el?.getAttribute('lb-default-price')) || 0);
      }, 0);
      // − / + counts packages (owner, 2026-10-08): the product and each ticked add-on go in at
      // this quantity. Selleasy's own per-row qty picker (enabled in Selleasy, hidden in
      // custom.css) is set to match, so Selleasy adds that many of each add-on itself.
      const qtyInput = this.closest('.cc-main-product')?.querySelector('.js-product-form-main [name="quantity"]');
      const qty = Math.max(1, Number(qtyInput?.value) || 1);
      document.querySelectorAll('.lb-widget-pal .lb-qty-count').forEach((input) => {
        if (input.value === String(qty)) return;
        input.value = qty;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      // A fresh panel (variant-picker.js swap) starts at 1: show the form's real quantity.
      const barQty = this.querySelector('.sticky-atc-panel__qty input');
      if (barQty && barQty.value !== String(qty)) barQty.value = qty;
      const price = qty * Number(this.dataset.price) + sum('.lb-price-sale-price', '[lb-default-price]', qty);
      const was = qty * Number(this.dataset.comparePrice) + sum('.lb-price-compare-price', '.lb-price-sale-price', qty);
      const isPackage = picked.length > 0;

      // Whole amounts without decimals ("1 447 kr"), others with both ("1 349,80 kr").
      const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'sv', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'SEK',
        minimumFractionDigits: cents % 100 ? 2 : 0,
        maximumFractionDigits: cents % 100 ? 2 : 0
      }).format(cents / 100);

      // The page's one buy button (by the price) becomes the package button: "Lägg paket i
      // varukorgen" plus the total (own line on mobile, custom.css). Checked on every poll, not
      // behind the key below: product-form.js puts the plain label back after "Tillagd".
      const inline = this.closest('.cc-main-product')?.querySelector('.js-product-form-main button[name="add"]');
      if (inline && !inline.disabled && !inline.matches('.is-added, .is-added-out')) {
        const want = isPackage ? money(price) : '';
        const ok = isPackage
          ? inline.dataset.packagePrice === want && inline.querySelector('.btn__package-price')
          : inline.textContent.trim() === inline.dataset.addToCartText;
        if (!ok) {
          inline.textContent = isPackage ? this.dataset.packageText : inline.dataset.addToCartText;
          inline.dataset.packagePrice = want;
          if (isPackage) {
            const total = document.createElement('span');
            total.className = 'btn__package-price';
            total.textContent = want;
            inline.append(total);
          }
        }
      }

      // Polled while Selleasy settles: only redraw the panel when something changed.
      const key = `${qty}|${price}|${picked.map((card) => card.getAttribute('lb-variant-id')).join(',')}`;
      if (this.packageKey === key) return;
      this.packageKey = key;

      this.classList.toggle('sticky-atc-panel--package', isPackage);
      this.applyState();
      pkg.hidden = !isPackage;
      btn.textContent = isPackage ? this.dataset.packageText : btn.dataset.defaultText;
      media?.querySelectorAll('.sticky-atc-panel__thumb').forEach((el) => el.remove());
      if (!isPackage) return;
      const allQty = picked.every((card) => card.querySelector('.lb-qty-count'));
      pkg.querySelector('.js-package-label-qty').textContent = qty > 1 && allQty ? ` × ${qty}` : '';
      pkg.querySelector('.js-package-price').textContent = money(price);
      pkg.querySelector('.js-package-was').textContent = was > price ? money(was) : '';

      picked.forEach((card) => {
        const src = card.querySelector('.lb-card-img')?.src;
        if (!src || !media) return;
        const img = document.createElement('img');
        img.className = 'sticky-atc-panel__thumb';
        img.src = src;
        img.alt = '';
        media.append(img);
      });
    }

    /**
     * The panel's buy button only shows once the main buy form has scrolled above the viewport,
     * so the page never shows two buy buttons at once (mobile and desktop). Mobile bar
     * (sticky-atc-panel--bar) with a Selleasy add-on ticked: the info row ("Paketpris" + total)
     * stays at the bottom the whole time, without its buttons until the form is passed.
     */
    applyState() {
      const past = Boolean(this.pastForm);
      const infoOnly = !past && !theme.mediaMatches.md
        && this.classList.contains('sticky-atc-panel--bar')
        && this.classList.contains('sticky-atc-panel--package');
      this.classList.toggle('sticky-atc-panel--info-only', infoOnly);
      this.classList.toggle('sticky-atc-panel--out', !past && !infoOnly);
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this.productForm) return;
          // isIntersecting, not just the bottom edge: scrolling back up slowly can fire with the
          // form's bottom exactly at 0 while it is re-entering, which read as "past" and left the
          // panel showing for good - no further callback comes while the form stays in view.
          this.pastForm = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          this.applyState();
        });
      });

      if (this.productForm) observer.observe(this.productForm);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
